import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FieldWrapper from 'components/Form/FieldWrapper';
import { Field, useFormikContext } from 'formik';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { LABEL_POSITIONS } from '../../../../../components/Form/_constants';
import { Subtitle1 } from '../../../../../components/UI/MDC/Typography/Typography';
import { CSS_CLASSES, FORM_KEYS } from '../../_constants';
import * as classes from '../../Agreements.module.scss';
import { getDepartmentNames as getDepartmentOptions } from './_api';

/**
 * Display auto-selected sponsoring department or drop down with possible options.
 * @param {string} departmentId The department ID selected by the user.
 * @returns {ReactElement} Either nothing, a label with the auto-selected sponsoring department, or a drop down with two or more possible sponsoring departments.
 */
const SponsoringDepartmentDropdown = ({ departmentId }) => {
	const [departmentOptions, setDepartmentOptions] = useState([]);
	const { setFieldValue } = useFormikContext();
	const setFieldValueCallback = useCallback(
		(field, value) => setFieldValue(field, value),
		[setFieldValue]
	);

	// useEffect hook retrieves department options whenever departmentId is updated
	useEffect(() => {
		if (departmentId != null && departmentId !== '') {
			getDepartmentOptions(departmentId).then(payload => {
				setDepartmentOptions(
					payload
						.map((department, index) => ({
							Key: index,
							Value: department.Value.trim(),
						}))
						.filter(
							(obj, index, self) =>
								self.findIndex(
									selfObj => selfObj.Value === obj.Value
								) === index
						)
				);
			});
		} else {
			// Reset options
			setDepartmentOptions([]);
		}
	}, [departmentId]);

	// useEffect hook updates field value if there is only one department option
	useEffect(() => {
		if (departmentOptions != null && departmentOptions.length === 1) {
			// Run formik setFieldValue function to set the value of sponsoring department
			setFieldValueCallback(
				FORM_KEYS.SPONSORING_DEPARTMENT,
				departmentOptions[0].Value
			);
		} else {
			// Run formik setFieldValue function to reset the value of sponsoring department
			setFieldValueCallback(FORM_KEYS.SPONSORING_DEPARTMENT, '');
		}
	}, [departmentOptions, setFieldValueCallback]);

	// Display appropriate component based on number of options
	switch (departmentOptions.length) {
		case 0:
			return null;
		case 1:
			// Return React component displaying name of sponsoring department
			return (
				<Subtitle1
					name={FORM_KEYS.SPONSORING_DEPARTMENT}
					className={classes[CSS_CLASSES.LABEL_AGREEMENT_TEXT]}
				>
					Sponsoring Department: {departmentOptions[0].Value}
				</Subtitle1>
			);
		default:
			// Return drop down component
			return (
				<Field name={FORM_KEYS.SPONSORING_DEPARTMENT}>
					{({ field, meta }) => (
						<FieldWrapper
							name={FORM_KEYS.SPONSORING_DEPARTMENT}
							label={'Select Sponsoring Department'}
							meta={meta}
							additionalProps={{
								labelPosition: LABEL_POSITIONS.TOP,
							}}
							hideErrorMessage
						>
							<FormControl
								sx={{
									width: '99%',
									marginLeft: '12px',
									marginRight: '12px',
								}}
								error={meta.touched && meta.error}
								required
								variant='filled'
							>
								<InputLabel
									id={`${FORM_KEYS.SPONSORING_DEPARTMENT}__label`}
								>
									Sponsoring Department
								</InputLabel>
								<Select
									id={FORM_KEYS.SPONSORING_DEPARTMENT}
									error={meta.touched && meta.error}
									fullWidth
									label='Sponsoring Department'
									labelId={`${FORM_KEYS.SPONSORING_DEPARTMENT}__label`}
									name={FORM_KEYS.SPONSORING_DEPARTMENT}
									onBlur={field.onBlur}
									onChange={field.onChange}
									value={field.value}
									variant='filled'
								>
									{departmentOptions.map(department => (
										<MenuItem
											key={department.Key}
											value={department.Value}
										>
											{department.Value}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									{meta.touched && meta.error
										? meta.error
										: ' '}
								</FormHelperText>
							</FormControl>
						</FieldWrapper>
					)}
				</Field>
			);
	}
};

export { SponsoringDepartmentDropdown };
